// src/pages/OrderOnTheWay.jsx
import React, { useState, useEffect } from "react";
import {
  collection,
  query,
  where,
  onSnapshot,
  doc,
  getDoc,
} from "firebase/firestore";
import { db } from "../services/firebase";
import Layout from "../component/Layout";

// ─── helpers ────────────────────────────────────────────────────────────────

const toMs = (expiresAt) => {
  if (!expiresAt) return 0;
  if (expiresAt.seconds) return expiresAt.seconds * 1000;
  return Number(expiresAt);
};

const isOtpExpired = (expiresAt) => !expiresAt || Date.now() > toMs(expiresAt);

const formatExpiry = (expiresAt) => {
  const diff = toMs(expiresAt) - Date.now();
  if (diff <= 0) return "Expired";
  const mins = Math.floor(diff / 60000);
  const secs = Math.floor((diff % 60000) / 1000);
  return `${mins}m ${secs}s`;
};

const formatDate = (date) => (date ? date.toLocaleString("en-IN") : "—");

const getAge = (orderTime) => {
  if (!orderTime) return "—";
  const diff = Date.now() - orderTime;
  return `${Math.floor(diff / 60000)}m ${Math.floor((diff % 60000) / 1000)}s`;
};

const getStatusStyle = (status) => ({
  display: "inline-block",
  padding: "2px 6px",
  fontSize: "11px",
  fontWeight: "bold",
  borderRadius: "4px",
  textTransform: "capitalize",
  backgroundColor:
    status === "Requested" ? "#fef3c7" :
    status === "Accepted"  ? "#dcfce7" :
    status === "Assorted"  ? "#ede9fe" : "#e5e7eb",
  color:
    status === "Requested" ? "#92400e" :
    status === "Accepted"  ? "#166534" :
    status === "Assorted"  ? "#5b21b6" : "#374151",
});

const getSizeRows = (order) => {
  const variants = order.selectedVariants || [];
  const lots     = order.lotDetails || [];
  const lotBySize = {};
  lots.forEach((l) => { if (l.size != null) lotBySize[l.size] = l; });
  if (variants.length) {
    return variants.map((v) => {
      const lot = lotBySize[v.size];
      return { size: v.size, qty: v.quantity, netWt: lot?.netWt ?? order.netWt, grossWt: lot?.grossWt ?? order.grossWt };
    });
  }
  if (lots.length) {
    return lots.map((l) => ({ size: l.size, qty: l.set, netWt: l.netWt, grossWt: l.grossWt }));
  }
  return [];
};

/**
 * Read the otpReference from the group's orders.
 * SellDeliver's writeOtpReferenceToOrders writes the same otpId to every
 * order in the group the moment the JRE clicks "Generate OTP".
 * Returns the first non-empty value found.
 */
const getGroupOtpReference = (orders) => {
  for (const order of orders) {
    if (order.otpReference) return order.otpReference;
  }
  return null;
};

/**
 * Fallback OTP lookup when otpReference hasn't propagated yet.
 * Scans the full otpData map and matches by params.sellerId + params.jreId
 * (both written by SellDeliver into the OTP doc's params field).
 * Returns the most-recent matching OTP document that has a displayOtp.
 */
const findOtpByParams = (otpData, sellerId, jreId) => {
  if (!sellerId || !jreId) return null;

  let bestId   = null;
  let bestDoc  = null;
  let bestTime = 0;

  for (const [id, doc] of Object.entries(otpData)) {
    // Must be an OPERATOR_GOODS type OTP
    const isOperatorGoods =
      doc.useCase === "OPERATOR_GOODS" ||
      doc.useCase === "JMI_OPERATOR_GOODS_OTP" ||
      doc.templateName === "JMI_OPERATOR_GOODS_OTP";

    if (!isOperatorGoods) continue;
    if (!doc.displayOtp)  continue;

    // Match by params written by SellDeliver
    const pSeller = doc.params?.sellerId || doc.params?.targetId || doc.sellerId || "";
    const pJre    = doc.params?.jreId    || doc.jreId    || "";

    if (pSeller !== sellerId || pJre !== jreId) continue;

    const t = doc.createdAt?.seconds ?? 0;
    if (t > bestTime) { bestTime = t; bestId = id; bestDoc = doc; }
  }

  return bestId ? { id: bestId, doc: bestDoc } : null;
};

// ─── component ───────────────────────────────────────────────────────────────

const OrderOnTheWay = () => {
  const [groupedOrders, setGroupedOrders] = useState({});
   const operatorId = localStorage.getItem("operatorId");
  const [otpData, setOtpData]             = useState({});
  const [jreMobiles, setJreMobiles]       = useState({});
  // Tick every second so expiry countdowns stay live
  const [, setTick] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, []);

  // ── Live OTP collection listener ──────────────────────────────────────────
  useEffect(() => {
    const unsub = onSnapshot(collection(db, "otps"), (snap) => {
      const map = {};
      snap.forEach((d) => { map[d.id] = d.data(); });
      setOtpData(map);
    });
    return unsub;
  }, []);

  // ── Live orders listener — PickedUp status ────────────────────────────────
  useEffect(() => {
    // Scope strictly to this operator; Firestore throws if a where() value is
    // undefined, so wait until the operator is available.
    if (!operatorId) {
      setGroupedOrders({});
      return undefined;
    }

    const q = query(
      collection(db, "orderList"),
      where("operatorId", "==", operatorId),
      where("orderStatus", "==", "PickedUp"),
    );
    const unsub = onSnapshot(q, (snap) => {
      const ordersData = snap.docs.map((docSnap) => {
        const data = docSnap.data();
        let orderTime = null;
        if (data.orderTime?.seconds)  orderTime = new Date(data.orderTime.seconds * 1000);
        else if (data.addedAt)        orderTime = new Date(data.addedAt);
        return { docId: docSnap.id, ...data, orderTime };
      });

      const groups = {};
      ordersData.forEach((order) => {
        const seller   = order.sellerId || "Unknown Seller";
        const jreId    = order.jreId    || "No JRE";
        const groupKey = `${seller}__${jreId}`;

        if (!groups[groupKey]) {
          groups[groupKey] = {
            seller,
            sellerId: order.sellerId,
            jreId,
            jreMobile: order.jreMobile || order.jreContact || order.jrePrimaryMobile || "",
            orders: [],
          };
        }
        groups[groupKey].orders.push(order);
      });

      Object.values(groups).forEach((g) =>
        g.orders.sort((a, b) => (a.orderTime || 0) - (b.orderTime || 0)),
      );

      setGroupedOrders(groups);
    });
    return unsub;
  }, [operatorId]);

  // ── Fetch missing JRE mobiles ─────────────────────────────────────────────
  useEffect(() => {
    const missing = [
      ...new Set(
        Object.values(groupedOrders)
          .filter((g) => g.jreId && g.jreId !== "No JRE" && !g.jreMobile && jreMobiles[g.jreId] === undefined)
          .map((g) => g.jreId),
      ),
    ];
    if (!missing.length) return;

    let cancelled = false;
    Promise.all(
      missing.map(async (jreId) => {
        try {
          const snap = await getDoc(doc(db, "jreregistrations", jreId));
          if (!snap.exists()) return [jreId, ""];
          const d = snap.data();
          return [jreId, d.primaryMobile || d.mobileNumber || d.contactNumber || d.phone || d.transactionalMobile || ""];
        } catch { return [jreId, ""]; }
      }),
    ).then((entries) => {
      if (cancelled) return;
      setJreMobiles((prev) => {
        const next = { ...prev };
        entries.forEach(([id, mob]) => { next[id] = mob || null; });
        return next;
      });
    });
    return () => { cancelled = true; };
  }, [groupedOrders, jreMobiles]);

  // ── Render ─────────────────────────────────────────────────────────────────
  const groupEntries = Object.entries(groupedOrders);

  return (
    <Layout pageTitle="Orders On The Way">
      <div style={{ padding: "16px" }}>
        {groupEntries.length === 0 ? (
          <p style={{ textAlign: "center", color: "#6b7280", paddingTop: "32px", fontSize: "18px" }}>
            No orders on the way.
          </p>
        ) : (
          groupEntries.map(([groupKey, group]) => {

            // ── Resolve OTP — two-path lookup ──────────────────────────
            // Fast path: read otpReference directly off the order docs.
            // SellDeliver writes it the moment the JRE triggers the OTP.
            // Fallback: scan otpData by params.sellerId + params.jreId in case
            // the Firestore write hasn't propagated to this snapshot yet.
            const otpRefId = getGroupOtpReference(group.orders);
            let otpDoc     = otpRefId ? otpData[otpRefId] : null;
            let resolvedId = otpRefId;

            if (!otpDoc) {
              const fallback = findOtpByParams(otpData, group.sellerId, group.jreId);
              if (fallback) { otpDoc = fallback.doc; resolvedId = fallback.id; }
            }

            // displayOtp is the plain 6-digit code stored by the cloud function
            // for no-SMS use cases (OPERATOR_GOODS).
            const visibleOtp = otpDoc?.displayOtp || "";

            const isVerified    = !!(otpDoc?.verified || otpDoc?.status === "verified");
            const isExpired     = otpDoc ? isOtpExpired(otpDoc.expiresAt) : false;
            const hasValidOtp   = !!(otpDoc && visibleOtp && !isExpired && !isVerified);
            const waitingForOtp = !otpDoc;

            const borderColor =
              hasValidOtp  ? "#3b82f6" :
              isVerified   ? "#10b981" :
              isExpired    ? "#ef4444" : "#6b7280";

            const cardBg =
              hasValidOtp  ? "#f0f9ff" :
              isVerified   ? "#f0fdf4" : "#ffffff";

            return (
              <div
                key={groupKey}
                style={{
                  marginBottom: "16px",
                  border: "1px solid #d1d5db",
                  borderLeft: `5px solid ${borderColor}`,
                  borderRadius: "8px",
                  padding: "16px",
                  backgroundColor: cardBg,
                }}
              >
                {/* ── Group header ── */}
                <div style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  flexWrap: "wrap",
                  gap: "16px",
                  marginBottom: "16px",
                }}>
                  <div style={{ flex: 1 }}>
                    <h3 style={{ margin: "0 0 6px", color: "#1f2937", fontSize: "20px", fontWeight: "bold" }}>
                      {group.seller}
                    </h3>
                    <div style={{ display: "flex", gap: "16px", flexWrap: "wrap", alignItems: "center" }}>
                      <span style={{ color: "#6b7280", fontSize: "13px" }}>
                        <strong>JRE:</strong> {group.jreId}
                      </span>
                      <span style={{ color: "#6b7280", fontSize: "13px" }}>
                        <strong>Orders:</strong> {group.orders.length}
                      </span>
                      {resolvedId && (
                        <span style={{ color: "#9ca3af", fontSize: "10px", fontFamily: "monospace" }}>
                          ref: {resolvedId.length > 40 ? resolvedId.slice(0, 40) + "…" : resolvedId}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* ── OTP badge ── */}
                  <div style={{ minWidth: "260px" }}>

                    {/* Waiting — JRE hasn't triggered OTP yet */}
                    {waitingForOtp && (
                      <div style={{
                        background: "#f3f4f6",
                        border: "1px dashed #9ca3af",
                        color: "#6b7280",
                        padding: "16px",
                        borderRadius: "8px",
                        textAlign: "center",
                      }}>
                        <div style={{ fontSize: "22px", marginBottom: "4px" }}>⏳</div>
                        <div style={{ fontSize: "13px", fontWeight: "600" }}>Waiting for JRE to generate OTP</div>
                        <div style={{ fontSize: "11px", marginTop: "4px", opacity: 0.75 }}>
                          The OTP will appear here automatically once the JRE triggers it
                        </div>
                      </div>
                    )}

                    {/* Live OTP — show the 6-digit code large and clear */}
                    {hasValidOtp && (
                      <div style={{
                        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                        color: "white",
                        padding: "20px 16px",
                        borderRadius: "8px",
                        textAlign: "center",
                        boxShadow: "0 4px 16px rgba(102,126,234,0.35)",
                      }}>
                        <div style={{ fontSize: "11px", opacity: 0.85, marginBottom: "6px", letterSpacing: "2px", textTransform: "uppercase" }}>
                          Delivery OTP
                        </div>
                        <div style={{
                          fontSize: "40px",
                          fontWeight: "bold",
                          letterSpacing: "12px",
                          fontFamily: "monospace",
                          marginBottom: "10px",
                          textShadow: "0 2px 4px rgba(0,0,0,0.2)",
                        }}>
                          {visibleOtp}
                        </div>
                        <div style={{ fontSize: "12px", opacity: 0.85 }}>
                          ⏱ Expires in: <strong>{formatExpiry(otpDoc.expiresAt)}</strong>
                        </div>
                        <div style={{ fontSize: "11px", opacity: 0.7, marginTop: "6px" }}>
                          Share this code with the JRE after verifying goods
                        </div>
                      </div>
                    )}

                    {/* Verified */}
                    {isVerified && (
                      <div style={{
                        background: "#10b981",
                        color: "white",
                        padding: "16px",
                        borderRadius: "8px",
                        textAlign: "center",
                      }}>
                        <div style={{ fontSize: "20px", fontWeight: "bold", marginBottom: "4px" }}>✅ Delivery Verified</div>
                        <div style={{ fontSize: "12px", opacity: 0.9 }}>
                          {otpDoc?.verifiedAt?.seconds
                            ? new Date(otpDoc.verifiedAt.seconds * 1000).toLocaleString("en-IN")
                            : "Verified"}
                        </div>
                      </div>
                    )}

                    {/* Expired — not yet verified */}
                    {!waitingForOtp && isExpired && !isVerified && (
                      <div style={{
                        background: "#ef4444",
                        color: "white",
                        padding: "16px",
                        borderRadius: "8px",
                        textAlign: "center",
                      }}>
                        <div style={{ fontSize: "16px", fontWeight: "bold", marginBottom: "4px" }}>❌ OTP Expired</div>
                        <div style={{ fontSize: "12px", opacity: 0.9 }}>Ask the JRE to generate a new OTP</div>
                      </div>
                    )}
                  </div>
                </div>

                {/* ── Orders table ── */}
                <div style={{ overflowX: "auto" }}>
                  <table style={{ minWidth: "100%", borderCollapse: "collapse", border: "1px solid #1f2937" }}>
                    <thead>
                      <tr style={{ backgroundColor: "#f3f4f6" }}>
                        {["Order Time", "Age", "Product", "Image", "Buyer", "Sizes", "Total Qty", "Net Wt (g)", "Gross Wt (g)", "Status"].map((h) => (
                          <th key={h} style={thStyle}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {group.orders.map((order) => {
                        const sizeRows = getSizeRows(order);
                        const isOverdue = order.orderTime && Date.now() - order.orderTime > 60000;
                        return (
                          <React.Fragment key={order.docId}>
                            <tr style={{
                              backgroundColor: isOverdue ? "#fee2e2" : "#ffffff",
                              outline: isOverdue ? "2px solid #fecaca" : "none",
                              outlineOffset: "-2px",
                            }}>
                              <td style={tdStyle}>{formatDate(order.orderTime)}</td>
                              <td style={{ ...tdStyle, fontFamily: "monospace" }}>{getAge(order.orderTime)}</td>
                              <td style={{ ...tdStyle, fontWeight: "bold" }}>{order.productName || "—"}</td>
                              <td style={tdStyle}>
                                {order.images?.[0]?.url
                                  ? <img src={order.images[0].url.trim()} alt="Product" style={{ height: "24px", width: "24px", objectFit: "contain", border: "1px solid #d1d5db", borderRadius: "3px" }} />
                                  : <span style={{ color: "#9ca3af" }}>—</span>}
                              </td>
                              <td style={tdStyle}>{order.buyerId || "—"}</td>
                              <td style={tdStyle}>
                                {sizeRows.length ? sizeRows.map((r, i) => <div key={i}>{r.size}</div>) : "—"}
                              </td>
                              <td style={tdStyle}>
                                {sizeRows.length
                                  ? sizeRows.reduce((s, r) => s + (parseFloat(r.qty) || 0), 0).toFixed(2)
                                  : "—"}
                              </td>
                              <td style={tdStyle}>{order.netWt || "—"}</td>
                              <td style={tdStyle}>{order.grossWt || "—"}</td>
                              <td style={tdStyle}>
                                <span style={getStatusStyle(order.orderStatus)}>{order.orderStatus}</span>
                              </td>
                            </tr>

                            {sizeRows.map((r, i) => (
                              <tr key={`${order.docId}-size-${i}`} style={{ backgroundColor: "#fafafa", fontSize: "11px" }}>
                                <td style={{ ...tdStyle, border: "none", padding: "2px 6px" }} />
                                <td style={{ ...tdStyle, border: "none", padding: "2px 6px" }} />
                                <td style={{ ...tdStyle, border: "none", color: "#6b7280", paddingLeft: "16px" }} colSpan={2}>↳ {r.size}</td>
                                <td style={{ ...tdStyle, border: "none", color: "#6b7280", padding: "2px 6px" }}>{r.qty}</td>
                                <td style={{ ...tdStyle, border: "none", color: "#6b7280", padding: "2px 6px" }}>
                                  {r.netWt != null ? parseFloat(r.netWt).toFixed(3) : "—"}
                                </td>
                                <td style={{ ...tdStyle, border: "none", color: "#6b7280", padding: "2px 6px" }}>
                                  {r.grossWt != null ? parseFloat(r.grossWt).toFixed(3) : "—"}
                                </td>
                                <td style={{ ...tdStyle, border: "none" }} colSpan={3} />
                              </tr>
                            ))}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* ── Action banner when OTP is live ── */}
                {hasValidOtp && (
                  <div style={{
                    marginTop: "12px",
                    padding: "12px 16px",
                    backgroundColor: "#dbeafe",
                    border: "1px solid #93c5fd",
                    borderRadius: "6px",
                    fontSize: "13px",
                    color: "#1e40af",
                  }}>
                    <strong>Action required:</strong>
                    <ul style={{ margin: "6px 0 0", paddingLeft: "20px" }}>
                      <li>JRE <strong>{group.jreId}</strong> is arriving with <strong>{group.orders.length}</strong> order{group.orders.length !== 1 ? "s" : ""}.</li>
                      <li>Verify all delivered products match the table above.</li>
                      <li>
                        Share OTP{" "}
                        <strong style={{ fontSize: "16px", letterSpacing: "4px", fontFamily: "monospace" }}>
                          {visibleOtp}
                        </strong>{" "}
                        with the JRE only after verification.
                      </li>
                    </ul>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </Layout>
  );
};

// ─── styles ──────────────────────────────────────────────────────────────────

const thStyle = {
  border: "1px solid #1f2937",
  padding: "4px 8px",
  textAlign: "left",
  fontSize: "11px",
  fontWeight: "bold",
  backgroundColor: "#f3f4f6",
  color: "#4b5563",
};

const tdStyle = {
  border: "1px solid #1f2937",
  padding: "4px 8px",
  fontSize: "12px",
};

export default OrderOnTheWay;
